import { Box, useTheme } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { DICOMApprovalStatus } from '~/domain/dicom/dicomConstants';
import { approvalStatusMark } from './approvalStatusMark';

/**
 * The glyph of an approval status at `size` px, in the colour of the status.
 * It carries the status as `data-approval-status`.
 */
export default function ApprovalMark({
    status,
    size,
}: {
    status: DICOMApprovalStatus;
    size: number;
}) {
    const { palette } = useTheme();
    const { Icon, labelId } = approvalStatusMark(status);
    const color = {
        approved: palette.success.main,
        rejected: palette.error.main,
        unapproved: palette.warning.main,
    }[labelId];
    return (
        <Icon
            // Width and height, not only the font size: a menu row sets its
            // icons' font size over the icon's own.
            sx={{ fontSize: size, width: size, height: size }}
            htmlColor={color}
            data-approval-status={status}
        />
    );
}

/**
 * An approval status as its glyph at `size` px, then its word. The word's
 * baseline is the label's, so a row that lines up on text baselines lines
 * it up with its neighbours; the glyph is centred on the word.
 */
export function ApprovalStatusLabel({
    status,
    size,
    'data-testid': testId,
}: {
    status: DICOMApprovalStatus;
    size: number;
    'data-testid'?: string;
}) {
    return (
        <Box
            component="span"
            display="inline-flex"
            alignItems="center"
            gap={0.5}
            minWidth={0}
        >
            <ApprovalMark status={status} size={size} />
            <Box component="span" alignSelf="baseline" data-testid={testId}>
                <FormattedMessage id={approvalStatusMark(status).labelId} />
            </Box>
        </Box>
    );
}
